import React, { useState, useRef, useEffect, useCallback, useLayoutEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { Header, Filter } from './types';
import { useFilterBuilder } from './useFilterBuilder';
import FieldsDropdown from './FieldsDropdown';
import FiltersDropdown from './FiltersDropdown';
import SelectOptionsDropdown from './SelectOptionsDropdown';
import * as S from './FilterMenu.style';
import CurrencyInput from '../CurrencyInput';
import { FeedbackContext } from '../../contexts/FeedbackContext';

interface FilterMenuProps {
  headers: Header[];
  onApplyFilter: (filter: Filter) => void;
  onClose: () => void;
  filters?: Filter[];
  anchorRef: React.MutableRefObject<HTMLDivElement | null>;
  align?: 'center' | 'start' | 'end';
}

type MenuPosition = {
  top: number;
  left: number;
  transform: string;
};

const VIEWPORT_PADDING = 12;
const MENU_ESTIMATED_WIDTH = 280;
const MENU_ESTIMATED_HEIGHT = 300;

const applyTimeMask = (rawValue: string): string => {
  const digits = rawValue.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

const timeMaskToSeconds = (maskedValue: string): number => {
  const [hoursPart, minutesPart] = maskedValue.split(':');
  return Number(hoursPart || 0) * 3600 + Number(minutesPart || 0) * 60;
};

const computeMenuPosition = (
  anchorRect: DOMRect,
  menuWidth: number,
  menuHeight: number,
  align: 'center' | 'start' | 'end',
): MenuPosition => {
  let top = anchorRect.bottom + 10;
  let left = anchorRect.left + anchorRect.width / 2;
  let transform = 'translateX(-50%)';

  const spaceBelow = window.innerHeight - anchorRect.bottom - 10;
  if (spaceBelow < menuHeight && anchorRect.top > menuHeight + 10) {
    top = anchorRect.top - menuHeight - 10;
  }

  if (align === 'end') {
    left = anchorRect.right;
    transform = 'translateX(-100%)';
    if (left - menuWidth < VIEWPORT_PADDING) {
      left = anchorRect.left;
      transform = 'none';
    }
  } else if (align === 'start') {
    left = anchorRect.left;
    transform = 'none';
    if (left + menuWidth > window.innerWidth - VIEWPORT_PADDING) {
      left = anchorRect.right;
      transform = 'translateX(-100%)';
    }
  } else {
    const halfWidth = menuWidth / 2;
    if (left - halfWidth < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING + halfWidth;
    } else if (left + halfWidth > window.innerWidth - VIEWPORT_PADDING) {
      left = window.innerWidth - VIEWPORT_PADDING - halfWidth;
    }
  }

  if (left + menuWidth > window.innerWidth - VIEWPORT_PADDING && transform === 'none') {
    left = window.innerWidth - VIEWPORT_PADDING - menuWidth;
  }
  if (transform === 'translateX(-100%)' && left - menuWidth < VIEWPORT_PADDING) {
    left = VIEWPORT_PADDING + menuWidth;
  }

  return { top, left, transform };
};

const defaultUnitOptions = [
  { value: 'KG', label: 'Kg' },
  { value: 'MM', label: 'MM' },
  { value: 'UN', label: 'UN' },
  { value: 'M²', label: 'M²' },
  { value: 'M Linear', label: 'M Linear' },
  { value: 'M', label: 'M' },
];

const FilterMenu: React.FC<FilterMenuProps> = ({
  headers,
  onApplyFilter,
  onClose,
  filters = [],
  anchorRef,
  align = 'center',
}) => {
  const {
    selectedField,
    selectedFilterType,
    filterValue,
    displayValue,
    setFilterValue,
    setDisplayValue,
    handleSelectField,
    handleSelectFilterType,
    handleApply: baseHandleApply
  } = useFilterBuilder({ onApplyFilter });
  const { setFeedback } = useContext(FeedbackContext);
  const [intervalValue, setIntervalValue] = useState({ start: '', end: '' });
  const [priceInputVal, setPriceInputVal] = useState<string>('');
  const [priceUnitVal, setPriceUnitVal] = useState<string>('');
  const [quantityInputVal, setQuantityInputVal] = useState<string>('');
  const [quantityUnitVal, setQuantityUnitVal] = useState<string>('');
  const [trackMode, setTrackMode] = useState<'tempo' | 'km'>('tempo');
  const [trackDays, setTrackDays] = useState<string>('');
  const [trackHours, setTrackHours] = useState<string>('');
  const [trackMinutes, setTrackMinutes] = useState<string>('');
  const [trackKm, setTrackKm] = useState<string>('');
  const [timeInputVal, setTimeInputVal] = useState<string>('');

  useEffect(() => {
    setPriceInputVal('');
    setPriceUnitVal('');
    setQuantityInputVal('');
    setQuantityUnitVal('');
    setTrackMode('tempo');
    setTrackDays('');
    setTrackHours('');
    setTrackMinutes('');
    setTrackKm('');
    setTimeInputVal('');
  }, [selectedField]);

  const handleApply = () => {
    if (!selectedField || !selectedFilterType) return;

    const resolveDisplayValue = (): string => {
      if (selectedField.type === 'quantity') return `${quantityInputVal}${quantityUnitVal ? ` ${quantityUnitVal}` : ''}`;
      if (selectedField.type === 'time') return timeInputVal;
      if (selectedField.type === 'price') {
        const fp = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(priceInputVal));
        return priceUnitVal ? `${fp}/${priceUnitVal}` : fp;
      }
      return displayValue || filterValue;
    };

    const resolveRawValue = (): string => {
      if (selectedField.type === 'quantity') return quantityInputVal;
      if (selectedField.type === 'time') return timeInputVal;
      if (selectedField.type === 'price') return priceInputVal;
      return filterValue;
    };

    const rawValue = resolveRawValue();
    if (rawValue) {
      const duplicateWithDifferentCondition = filters.find(
        f => f.key === selectedField.key && String(f.displayValue || f.value) === resolveDisplayValue() && f.filterType !== selectedFilterType
      ) || filters.find(
        f => f.key === selectedField.key && String(typeof f.value === 'object' ? (f.value.quantity || f.value.price || '') : f.value) === rawValue && f.filterType !== selectedFilterType
      );

      if (duplicateWithDifferentCondition) {
        setFeedback({
          isOpen: true,
          message: `O valor "${resolveDisplayValue()}" já foi adicionado na coluna "${selectedField.label}" com condição diferente. Não é possível aplicar para evitar conflito.`,
          statusOk: false,
        });
        return;
      }
    }

    if (selectedField.type === 'time') {
      if (!timeInputVal || timeInputVal.length < 5) return;
      const newFilter: Filter = {
        key: selectedField.key,
        label: selectedField.label,
        filterType: selectedFilterType,
        value: timeMaskToSeconds(timeInputVal),
        displayValue: timeInputVal,
        fieldType: 'number',
      };
      onApplyFilter(newFilter);
      setTimeInputVal('');
      return;
    }
    if (selectedField.type === 'quantity') {
      if (!quantityInputVal) return;
      const displayUnit = quantityUnitVal ? ` ${quantityUnitVal}` : '';
      const newFilter: Filter = {
        key: selectedField.key,
        label: selectedField.label,
        filterType: selectedFilterType,
        value: quantityUnitVal ? { quantity: quantityInputVal, unit: quantityUnitVal } : quantityInputVal,
        displayValue: `${quantityInputVal}${displayUnit}`,
        fieldType: 'quantity',
      };
      onApplyFilter(newFilter);
      setQuantityInputVal('');
      setQuantityUnitVal('');
      return;
    }
    if (selectedField.type === 'price') {
      if (!priceInputVal) return;
      const formattedPrice = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(parseFloat(priceInputVal));

      const newFilter: Filter = {
        key: selectedField.key,
        label: selectedField.label,
        filterType: selectedFilterType,
        value: priceUnitVal ? { price: priceInputVal, unit: priceUnitVal } : priceInputVal,
        displayValue: priceUnitVal ? `${formattedPrice}/${priceUnitVal}` : formattedPrice,
        fieldType: 'price',
      };
      onApplyFilter(newFilter);
      setPriceInputVal('');
      setPriceUnitVal('');
      return;
    }
    if (selectedField.type === 'date' && selectedFilterType === 'Intervalo') {
      if (!intervalValue.start || !intervalValue.end) return;
      const formatDateBR = (dateStr: string): string => {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          const [year, month, day] = parts;
          return `${day}/${month}/${year}`;
        }
        return dateStr;
      };
      const newFilter: Filter = {
        key: selectedField.key,
        label: selectedField.label,
        filterType: selectedFilterType,
        value: { start: intervalValue.start, end: intervalValue.end },
        displayValue: `${formatDateBR(intervalValue.start)} até ${formatDateBR(intervalValue.end)}`,
        fieldType: selectedField.type,
      };
      onApplyFilter(newFilter);
      setIntervalValue({ start: '', end: '' });
      return;
    }
    if (selectedField.key === 'track') {
      if (trackMode === 'tempo') {
        if (!trackDays && !trackHours && !trackMinutes) return;
        const parts: string[] = [];
        if (trackDays) parts.push(`${trackDays}d`);
        if (trackHours) parts.push(`${trackHours}h`);
        if (trackMinutes) parts.push(`${trackMinutes}min`);
        const newFilter: Filter = {
          key: 'trackTime',
          label: 'Tempo de trajeto',
          filterType: selectedFilterType,
          value: parts.join(''),
          displayValue: parts.join(' '),
          fieldType: 'string',
        };
        onApplyFilter(newFilter);
        setTrackDays(''); setTrackHours(''); setTrackMinutes('');
      } else {
        if (!trackKm) return;
        const newFilter: Filter = {
          key: 'trackKm',
          label: 'Tempo de trajeto',
          filterType: selectedFilterType,
          value: trackKm,
          displayValue: `${trackKm} km`,
          fieldType: 'number',
        };
        onApplyFilter(newFilter);
        setTrackKm('');
      }
      return;
    }
    baseHandleApply();
  };

  const [isFieldsDropdownOpen, setFieldsDropdownOpen] = useState(false);
  const [isFiltersDropdownOpen, setFiltersDropdownOpen] = useState(false);
  const [isSelectOptionsDropdownOpen, setSelectOptionsDropdownOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(() => {
    const anchor = anchorRef.current;
    if (!anchor) return null;
    return computeMenuPosition(anchor.getBoundingClientRect(), MENU_ESTIMATED_WIDTH, MENU_ESTIMATED_HEIGHT, align);
  });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;

    if (!anchor) {
      setMenuPosition(null);
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth ?? MENU_ESTIMATED_WIDTH;
    const menuHeight = menuRef.current?.offsetHeight ?? MENU_ESTIMATED_HEIGHT;
    setMenuPosition(computeMenuPosition(rect, menuWidth, menuHeight, align));
  }, [anchorRef, align]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition, isFieldsDropdownOpen, isFiltersDropdownOpen, isSelectOptionsDropdownOpen, selectedField, selectedFilterType]);

  useEffect(() => {
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const isClickOnAnchor = anchorRef.current && anchorRef.current.contains(event.target as Node);
      if (menuRef.current && !menuRef.current.contains(event.target as Node) && !isClickOnAnchor) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const onApplyAndClose = () => {
    handleApply();
    onClose();
  }

  const availableHeaders = headers;

  if (!menuPosition) {
    return null;
  }

  const menuContent = (
    <S.MenuContainer
      ref={menuRef}
      style={{
        top: menuPosition.top,
        left: menuPosition.left,
        transform: menuPosition.transform,
      }}
    >
      <h4>Construir Filtro</h4>
      <S.BuilderRow>
        <S.FilterSelectWrapper>
          <S.FilterInput
            readOnly
            value={selectedField?.label || ''}
            placeholder="Selecione um campo"
            onClick={() => setFieldsDropdownOpen(true)}
          />
        </S.FilterSelectWrapper>
      </S.BuilderRow>
      {isFieldsDropdownOpen && (
        <FieldsDropdown
          headers={availableHeaders}
          onSelect={(field) => {
            handleSelectField(field);
            setFieldsDropdownOpen(false);
          }}
          close={() => setFieldsDropdownOpen(false)}
        />
      )}

      {selectedField && selectedField.key !== 'companyTypes' && (
        <>
          <S.BuilderRow>
            <S.FilterSelectWrapper>
              <S.FilterInput
                readOnly
                value={selectedFilterType}
                placeholder="Selecione a condição"
                onClick={() => setFiltersDropdownOpen(true)}
              />
            </S.FilterSelectWrapper>
          </S.BuilderRow>
          {isFiltersDropdownOpen && (
            <FiltersDropdown
              fieldType={selectedField.type}
              fieldKey={selectedField.key}
              onSelect={(type) => {
                handleSelectFilterType(type);
                setFiltersDropdownOpen(false);
              }}
              close={() => setFiltersDropdownOpen(false)}
            />
          )}
        </>
      )}

      {selectedField && (
        selectedField.key === 'track' ? (
          <>
            <S.BuilderRow style={{ display: 'flex', gap: '4px', width: '100%' }}>
              <button
                type="button"
                onClick={() => setTrackMode('tempo')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: '20px',
                  border: 'none',
                  backgroundColor: trackMode === 'tempo' ? '#0090D3' : '#EBEBEB',
                  color: trackMode === 'tempo' ? '#fff' : '#696969',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >Tempo</button>
              <button
                type="button"
                onClick={() => setTrackMode('km')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: '20px',
                  border: 'none',
                  backgroundColor: trackMode === 'km' ? '#0090D3' : '#EBEBEB',
                  color: trackMode === 'km' ? '#fff' : '#696969',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >KM</button>
            </S.BuilderRow>
            {trackMode === 'tempo' ? (
              <S.BuilderRow style={{ display: 'flex', gap: '4px', width: '100%' }}>
                <S.FilterSelectWrapper style={{ flex: 1, minWidth: 0 }}>
                  <S.FilterInput
                    type="number"
                    placeholder="Dias"
                    value={trackDays}
                    onChange={e => setTrackDays(e.target.value)}
                    min="0"
                  />
                </S.FilterSelectWrapper>
                <S.FilterSelectWrapper style={{ flex: 1, minWidth: 0 }}>
                  <S.FilterInput
                    type="number"
                    placeholder="Horas"
                    value={trackHours}
                    onChange={e => setTrackHours(e.target.value)}
                    min="0"
                    max="23"
                  />
                </S.FilterSelectWrapper>
                <S.FilterSelectWrapper style={{ flex: 1, minWidth: 0 }}>
                  <S.FilterInput
                    type="number"
                    placeholder="Min"
                    value={trackMinutes}
                    onChange={e => setTrackMinutes(e.target.value)}
                    min="0"
                    max="59"
                  />
                </S.FilterSelectWrapper>
              </S.BuilderRow>
            ) : (
              <S.BuilderRow>
                <S.FilterSelectWrapper>
                  <S.FilterInput
                    type="number"
                    placeholder="KM"
                    value={trackKm}
                    onChange={e => setTrackKm(e.target.value)}
                    step="any"
                    min="0"
                  />
                </S.FilterSelectWrapper>
              </S.BuilderRow>
            )}
          </>
        ) : selectedField.selectOptions ? (
          <>
            <S.BuilderRow>
              <S.FilterSelectWrapper>
                <S.FilterInput
                  readOnly
                  value={
                    selectedField.selectOptions.find((option) => option.value === filterValue)
                      ?.label || ''
                  }
                  placeholder="Selecione"
                  onClick={() => setSelectOptionsDropdownOpen(true)}
                />
              </S.FilterSelectWrapper>
            </S.BuilderRow>
            {isSelectOptionsDropdownOpen && (
              <SelectOptionsDropdown
                options={selectedField.selectOptions.filter(opt =>
                  !filters.some(f => f.key === selectedField.key && (f.value === opt.value || f.displayValue === opt.label))
                )}
                onSelect={(option) => {
                  setFilterValue(option.value);
                  setDisplayValue(option.label);
                  handleSelectFilterType('Igual a');
                  setSelectOptionsDropdownOpen(false);
                }}
                close={() => setSelectOptionsDropdownOpen(false)}
              />
            )}
          </>
        ) : selectedField.type === 'date' && selectedFilterType === 'Intervalo' ? (
          <>
            <S.BuilderRow>
              <S.FilterSelectWrapper>
                <S.FilterInput
                  className="inline-date"
                  type="date"
                  placeholder="Data inicial"
                  value={intervalValue.start}
                  onChange={e => setIntervalValue(v => ({ ...v, start: e.target.value }))}
                />
              </S.FilterSelectWrapper>
            </S.BuilderRow>
            <S.BuilderRow>
              <S.FilterSelectWrapper>
                <S.FilterInput
                  className="inline-date"
                  type="date"
                  placeholder="Data final"
                  value={intervalValue.end}
                  onChange={e => setIntervalValue(v => ({ ...v, end: e.target.value }))}
                />
              </S.FilterSelectWrapper>
            </S.BuilderRow>
          </>
        ) : selectedField.type === 'date' ? (
          <S.BuilderRow>
            <S.FilterSelectWrapper>
              <S.FilterInput
                className="inline-date"
                type="date"
                placeholder="Selecione a data"
                value={filterValue}
                onChange={e => setFilterValue(e.target.value)}
              />
            </S.FilterSelectWrapper>
          </S.BuilderRow>
        ) : selectedField.type === 'time' ? (
          <S.BuilderRow>
            <S.FilterSelectWrapper>
              <S.FilterInput
                type="text"
                className="inline-input"
                placeholder="00:00"
                value={timeInputVal}
                onChange={e => setTimeInputVal(applyTimeMask(e.target.value))}
                inputMode="numeric"
                maxLength={5}
              />
            </S.FilterSelectWrapper>
          </S.BuilderRow>
        ) : selectedField.type === 'quantity' ? (
          <S.BuilderRow style={{ display: 'flex', gap: '8px', width: '100%' }}>
            <S.FilterSelectWrapper style={{ flex: 1, minWidth: 0 }}>
              <S.FilterInput
                type="number"
                placeholder="Digite aqui"
                value={quantityInputVal}
                onChange={e => setQuantityInputVal(e.target.value)}
                inputMode="decimal"
                step="any"
              />
            </S.FilterSelectWrapper>
            <select
              value={quantityUnitVal}
              onChange={e => setQuantityUnitVal(e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '20px',
                border: '1px solid transparent',
                backgroundColor: '#EBEBEB',
                color: '#696969',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                height: '40px',
                outline: 'none',
                width: '100px',
                flexShrink: 0
              }}
            >
              <option value="">Sem unid.</option>
              {(selectedField.unitOptions ?? defaultUnitOptions).map(unit => (
                <option key={unit.value} value={unit.value}>{unit.label}</option>
              ))}
            </select>
          </S.BuilderRow>
        ) : selectedField.type === 'price' ? (
          <S.BuilderRow style={{ display: 'flex', gap: '8px', width: '100%' }}>
            <CurrencyInput
              id="filter-price"
              name="filter-price"
              value={priceInputVal !== '' ? parseFloat(priceInputVal) : NaN}
              onValueChange={(formatted, __, values) => {
                if (values?.float === undefined) {
                  setPriceInputVal('');
                  return;
                }
                setPriceInputVal(values.float.toString());
              }}
              style={{ flex: 1, minWidth: 0 }}
            />
            <select
              value={priceUnitVal}
              onChange={e => setPriceUnitVal(e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '20px',
                border: '1px solid transparent',
                backgroundColor: '#EBEBEB',
                color: '#696969',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                height: '40px',
                outline: 'none',
                width: '100px',
                flexShrink: 0
              }}
            >
              <option value="">Sem unid.</option>
              {(selectedField.unitOptions ?? defaultUnitOptions).map(unit => (
                <option key={unit.value} value={unit.value}>{unit.label}</option>
              ))}
            </select>
          </S.BuilderRow>
        ) : selectedField.type === 'currency' ? (
          <S.BuilderRow>
            <S.FilterSelectWrapper>
              <CurrencyInput
                id="filter-currency"
                name="filter-currency"
                className="inline-input"
                value={filterValue !== '' ? parseFloat(filterValue) : NaN}
                onValueChange={(formatted, __, values) => {
                  if (values?.float === undefined) {
                    setFilterValue('');
                    setDisplayValue('');
                    return;
                  }
                  setFilterValue(values.float.toString());
                  setDisplayValue(values.formatted ?? formatted ?? '');
                }}
              />
            </S.FilterSelectWrapper>
          </S.BuilderRow>
        ) : selectedField.type === 'boolean' ? (
          <S.BuilderRow style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '0 8px' }}>
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#696969' }}>
              {filterValue === 'false' ? 'Inativo' : 'Ativo'}
            </span>
            <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
              <input
                type="checkbox"
                checked={filterValue !== 'false'}
                onChange={(e) => {
                  const val = e.target.checked ? 'true' : 'false';
                  setFilterValue(val);
                  setDisplayValue(e.target.checked ? 'Ativo' : 'Inativo');
                }}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span className="slider round" style={{
                position: 'absolute',
                cursor: 'pointer',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: filterValue === 'false' ? '#ccc' : '#10B981',
                transition: '0.4s',
                borderRadius: '24px'
              }}>
                <span style={{
                  position: 'absolute',
                  content: '""',
                  height: '18px', width: '18px',
                  left: filterValue === 'false' ? '3px' : '23px',
                  bottom: '3px',
                  backgroundColor: 'white',
                  transition: '0.4s',
                  borderRadius: '50%'
                }} />
              </span>
            </label>
          </S.BuilderRow>
        ) : (
          <S.BuilderRow>
            <S.FilterSelectWrapper>
              <S.FilterInput
                type={selectedField.type === 'number' ? 'number' : 'text'}
                className="inline-input"
                placeholder="Digite aqui"
                value={filterValue}
                onChange={e => setFilterValue(e.target.value)}
                inputMode={selectedField.type === 'number' ? 'decimal' : undefined}
                step={selectedField.type === 'number' ? 'any' : undefined}
              />
            </S.FilterSelectWrapper>
          </S.BuilderRow>
        )
      )}

      <S.ApplyButton onClick={onApplyAndClose}>Aplicar Filtro</S.ApplyButton>
    </S.MenuContainer>
  );

  return createPortal(menuContent, document.body);
};

export default FilterMenu;